// Components
import { FormElems, Alert, ConditionalComponent } from '../';

// Actions
import {
    createUserRequest,
    fetchEntityRequest,
    createUserError,
    createUserReset,
    updateUserReset,
    createAdminUserRequest,
    fetchSystemUsersRequest,
} from '../../containers/UserManagement/redux/userManagement.actions';

// Constant
import { getUserLevel, nameValueOptions } from '../../utils/helperMethods';
import {
    ENTITY_TYPE,
    EXCLUDED_ENTITY_USER_LEVELS,
    MAX_USER_FETCH_COUNT_USER_MANAGEMENT,
    USER_LEVEL_ENUM,
} from '../../utils/constants/commonValues';
import { USER_MANAGEMENT_MSG } from '../../utils/constants/toastMessages';

// Validator
import validate from './validator';

// Styles
import './createUserForm.scss';

import { fetchEntityListRequest } from 'containers/MerchantOnboard/redux/entity/entity.actions';
/* eslint-disable indent */
// Libraries
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Field, reduxForm, change } from 'redux-form';
import { compose, bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { toast } from 'react-toastify';
import { isEmpty } from 'lodash';

/**
 * CreateUserForm Component
 *
 * @param {Object} props - React component props
 */

const CreateUserForm = props => {
    const {
        handleSubmit,
        actions,
        closePopup,
        userManagementData,
        formData,
        fetchData,
        change,
        entityData
    } = props;
    const {
        createUser,
        userRoles,
        systemUserList,
    } = userManagementData;
    // Destructure entityData to get entityList and loading state
    const { entityList, loading } = entityData; 
    const entityType =
        formData && formData.values && formData.values.entityType;
    const uLevel = formData && formData.values && formData.values.uLevel;
    // Reference to the entityType to avoid stale closure issues
    const entityTypeRef = React.useRef(entityType);
    const [selectedEntity, setSelectedEntity] = useState(null);

    /* Effect is to fetch entity list */
    useEffect(() => {
        if (entityType && !EXCLUDED_ENTITY_USER_LEVELS.includes(entityType)) {
            actions.fetchEntityRequest({
                entityType,
            });
            // fetch the dropdown list
            actions.fetchEntityListRequest(entityType)
        } else if (
            entityType &&
            uLevel &&
            EXCLUDED_ENTITY_USER_LEVELS.includes(entityType)
        ) {
            if (uLevel !== 'L3') {
                actions.fetchSystemUsersRequest({
                    page: 1,
                    resultPerPage: MAX_USER_FETCH_COUNT_USER_MANAGEMENT,
                    userFilter: {
                        level: uLevel === 'L2' ? 'L3' : 'L2',
                    },
                });
            }
        }
    }, [actions, entityType, uLevel]);

    const userLevelOption = getUserLevel();

    /* Effect is to show the success message and reset redux error */
    useEffect(() => {
        if (createUser.isSuccess) {
            closePopup();
            fetchData({ pageIndex: 1, pageSize: 10 });
            toast.info(USER_MANAGEMENT_MSG.USER_CREATE);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [closePopup, createUser.isSuccess]);

    /* Effect to reset the redux values */
    useEffect(() => {
        // reset data
        return () => {
            actions.createUserReset();
            actions.updateUserReset();
        };
    }, [actions]);

    // Effect to update the entityTypeRef when entityType changes
    useEffect(() => {
        entityTypeRef.current = entityType;
        setSelectedEntity(null); // reset selection
    }, [entityType])

    const resetForm = () => {
        actions.createUserReset();
    };

    /**
     * Method to handle form submit values for create user
     * @param {object} values
     * @returns {null}
     */
    function handleFormSubmit(values) {
        if (values.entityType === 'ADMIN') {
            const request = {
                email: values.email || '',
                name: values.name || '',
                roleId: values.roleId || '',
                level: values.uLevel || null,
                parentUserId: values.supervisor || '',
            };
            actions.createAdminUserRequest(request);
        } else if (values.entityType === 'RM') {
            const request = {
                ...values,
                entityId: values.roleId,
                entityName: values.name,
                level: values.uLevel || null,
                parentUserId: values.supervisor || '',
            };
            actions.createUserRequest(request);
        } else {
            const entityName = entityList.find(
                entity => entity.entityCode === values.entityId
            ).name;

            const request = {
                ...values,
                entityName,
            };
            actions.createUserRequest(request);
        }
    }

    function resetEntityId() {
        change('entityId', '');
        change('uLevel', '');
    }

    useEffect(() => {
        // reset error message on unmount
        return () => {
            actions.createUserError({
                isLoading: false,
                message: '',
            });
        };
    }, [actions]);

    return (
        <form
            className="create-user-form"
            onChange={resetForm}
            onSubmit={handleSubmit(handleFormSubmit)}
        >
            <div className="form-section">
                <div className="form-field">
                    <p className="label">Email ID</p>
                    <Field
                        name="email"
                        type="text"
                        component={FormElems.Input}
                        placeholder="Enter Email ID"
                    />
                </div>
            </div>
            <div className="form-section">
                <div className="form-field" style={{ marginRight: '25px' }}>
                    <p className="label">Name</p>
                    <Field
                        name="name"
                        type="text"
                        component={FormElems.Input}
                        placeholder="Enter Name"
                    />
                </div>
                <div className="form-field">
                    <p className="label">Role</p>
                    <Field
                        name="roleId"
                        data={userRoles.list}
                        label="Select Role"
                        labelField="name"
                        valueField="value"
                        component={FormElems.Select}
                    />
                </div>
            </div>
            <div className="form-section">
                <div className="form-field user-level">
                    <p className="label">Select User Type</p>
                    <Field
                        name="entityType"
                        wrapperClass="buttonMode"
                        options={userLevelOption}
                        valueField="value"
                        labelField="name"
                        component={FormElems.Radio}
                        onChange={resetEntityId}
                    />
                </div>
            </div>
            <ConditionalComponent
                isVisible={
                    formData &&
                    formData.values &&
                    formData.values.entityType &&
                    EXCLUDED_ENTITY_USER_LEVELS.includes(
                        formData.values.entityType
                    )
                }
            >
                <div className="form-section">
                    <div className="form-field user-level">
                        <p className="label">Select User Level</p>
                        <Field
                            name="uLevel"
                            wrapperClass="buttonMode"
                            options={nameValueOptions(
                                Object.keys(USER_LEVEL_ENUM)
                            )}
                            valueField="value"
                            labelField="name"
                            component={FormElems.Radio}
                        />
                    </div>
                </div>
                <ConditionalComponent
                    isVisible={
                        formData &&
                        formData.values &&
                        formData.values.uLevel &&
                        formData.values.uLevel !== 'L3'
                    }
                >
                    <div className="form-section">
                        <div className="form-field">
                            <p className="label">Select Supervisor</p>
                            <Field
                                name="supervisor"
                                data={systemUserList}
                                label={`Select Supervisor`}
                                labelField="name"
                                valueField="value"
                                component={FormElems.Select}
                                placeholder="Enter Email ID"
                            />
                        </div>
                    </div>
                </ConditionalComponent>
            </ConditionalComponent>
            {formData &&
            formData.values &&
            formData.values.entityType &&
            !EXCLUDED_ENTITY_USER_LEVELS.includes(
                formData.values.entityType
            ) ? (
                <>
                <div className="form-section">
                    <div className="form-field">
                        <Field
                            name="entityId"
                            data={entityList} 
                            label={`${ENTITY_TYPE[formData.values.entityType]} Name`}
                            labelField="name"
                            valueField="entityCode"
                            component={FormElems.DynamicSelect}
                            onSearch={(entityName) => {
                                actions.fetchEntityListRequest(entityTypeRef.current, {entityName});
                            }}
                            placeholder="Enter Name"
                            optionsLoading={loading}
                            resetOn={entityType}
                            searchDelay={300}
                            onSelect={(option) => setSelectedEntity(option)} // Callback to handle selection
                            defaultOption={selectedEntity}
                        />
                        
                    </div>
                </div>
                <div className="form-section">
                    <div className="form-field">
                        <Field
                            name="entityCode"
                            data={entityList}
                            label={`${ENTITY_TYPE[formData.values.entityType]} ID`}
                            labelField="entityCode" // reverse label
                            valueField="name"       // reverse value
                            component={FormElems.DynamicSelect}
                            onSearch={(entityCode) => {
                                actions.fetchEntityListRequest(entityTypeRef.current, { entityCode });
                            }}
                            placeholder="Enter ID"
                            optionsLoading={loading}
                            resetOn={entityType}
                            searchDelay={300}
                            onSelect={(option) => setSelectedEntity(option)} // Callback to handle selection
                            defaultOption={selectedEntity}
                        />
                        
                    </div>
                </div>
                </>
            
            ) : null}
            {createUser.message ? <Alert>{createUser.message}</Alert> : null}
            <div className="form-section action">
                <div className="cancel-btn" onClick={() => closePopup()}>
                    CANCEL
                </div>
                <div className="submit-btn">
                    <FormElems.Button
                        buttonType="primary"
                        isDisable={formData && isEmpty(formData.values)}
                        text="SUBMIT"
                    />
                </div>
            </div>
        </form>
    );
};

const mapStateToProps = ({ auth, userManagementData, entityData, form }) => ({
    auth,
    userManagementData,
    entityData,
    formData: form['create-user'],
});

const mapDispatchToProps = dispatch => ({
    actions: bindActionCreators(
        {
            createUserRequest,
            fetchEntityRequest,
            fetchEntityListRequest,
            change,
            createUserError,
            createUserReset,
            updateUserReset,
            createAdminUserRequest,
            fetchSystemUsersRequest,
        },
        dispatch
    ),
});

CreateUserForm.propTypes = {
    actions: PropTypes.object,
    auth: PropTypes.object,
    closePopup: PropTypes.func,
    handleSubmit: PropTypes.func,
    userManagementData: PropTypes.object,
    formData: PropTypes.object,
    fetchData: PropTypes.func,
    change: PropTypes.func,
    entityData: PropTypes.object.isRequired,
};

export default compose(
    reduxForm({
        form: 'create-user',
        validate,
    }),
    connect(mapStateToProps, mapDispatchToProps)
)(CreateUserForm);
